/**
 * The strip map's fixed layer, painted once into an offscreen canvas: the ground that cannot change.
 *
 * ROUND5 §1 found four things the orders talk about and this layer never drew - the river with its
 * fords and its cracked ice, the objective's own zones, the marked minefields, and the circles a
 * mission is lost inside. `mapGround.backdropPlan` decides all of it without a canvas; this file only
 * puts it on one, so the decisions can be tested and the painting stays dumb.
 *
 * It also draws the two intel cards that are pictures of the ground: Recon Photos (the buildings) and
 * Patrol Schedule (the sentries' rounds). Both are static, so both belong on this layer.
 */

import { clamp } from './dom';
import {
  type BackdropPlan, backdropPlan, type MapCircle, type PipeMark, type StructureMark, type WaterMark,
} from './mapGround';
import { MAP_COLOURS } from './mapMarks';
import type { MissionDef, Polyline, Rect } from '../../sim/types';

/** The canvas the fixed layer is painted on, and how many pixels a metre is worth. */
interface Paper {
  g: CanvasRenderingContext2D;
  scale: number;
  width: number;
  height: number;
}

function strokeLines(p: Paper, lines: readonly Polyline[], colour: string, maxWidth: number): void {
  p.g.strokeStyle = colour;
  for (const line of lines) {
    p.g.lineWidth = clamp(line.width * p.scale, 1, maxWidth);
    p.g.beginPath();
    line.points.forEach(([x, z], i) => {
      if (i === 0) p.g.moveTo(x * p.scale, z * p.scale);
      else p.g.lineTo(x * p.scale, z * p.scale);
    });
    p.g.stroke();
  }
}

/** A dashed rectangle, kept at least 3 px across so a small zone is still a shape and not a dot. */
function dashRect(p: Paper, rect: Rect, colour: string, dash: readonly number[]): void {
  const x0 = clamp(Math.round(Math.min(rect.x0, rect.x1) * p.scale), 0, p.width - 3);
  const z0 = clamp(Math.round(Math.min(rect.z0, rect.z1) * p.scale), 0, p.height - 3);
  const x1 = clamp(Math.round(Math.max(rect.x0, rect.x1) * p.scale), x0 + 3, p.width);
  const z1 = clamp(Math.round(Math.max(rect.z0, rect.z1) * p.scale), z0 + 3, p.height);
  p.g.strokeStyle = colour;
  p.g.lineWidth = 1;
  p.g.setLineDash([...dash]);
  p.g.strokeRect(x0 + 0.5, z0 + 0.5, x1 - x0 - 1, z1 - z0 - 1);
  p.g.setLineDash([]);
}

/**
 * A water barrier: the band itself, then the lanes that cross it, then the ice that will not hold.
 * The order is the order a man reads it in - "there is a river, here is where you cross, not there".
 */
function drawWater(p: Paper, barriers: readonly WaterMark[]): void {
  for (const barrier of barriers) {
    const band = Math.max(1, barrier.course.width * p.scale);
    strokeLines(p, [barrier.course], barrier.surface === 'ice' ? MAP_COLOURS.ice : MAP_COLOURS.water, band);
    p.g.fillStyle = MAP_COLOURS.crossing;
    for (const crossing of barrier.crossings) {
      const w = Math.max(1, crossing.halfWidth * 2 * p.scale);
      p.g.fillRect(crossing.x * p.scale - w / 2, crossing.z * p.scale - band / 2, w, band);
    }
    p.g.fillStyle = MAP_COLOURS.weakIce;
    for (const weak of barrier.weakIce) {
      p.g.beginPath();
      p.g.arc(weak.x * p.scale, weak.z * p.scale, Math.max(1, weak.r * p.scale), 0, Math.PI * 2);
      p.g.fill();
    }
  }
}

/**
 * The pipe as steel with its supports left open, and each support given the same pale lane a ford gets:
 * both are the one place a barrier lets a man through, so both say so the same way (ROUND5 §2.6).
 */
function drawPipes(p: Paper, pipes: readonly PipeMark[]): void {
  for (const pipe of pipes) {
    const z = clamp(Math.round(pipe.z * p.scale), 1, p.height - 2);
    p.g.fillStyle = MAP_COLOURS.pipe;
    for (const [x0, x1] of pipe.runs) {
      const from = Math.round(x0 * p.scale);
      p.g.fillRect(from, z, Math.max(1, Math.round(x1 * p.scale) - from), 1);
    }
    p.g.fillStyle = MAP_COLOURS.crossing;
    for (const [x0, x1] of pipe.gaps) {
      const from = Math.round(x0 * p.scale);
      p.g.fillRect(from, z - 1, Math.max(1, Math.round(x1 * p.scale) - from), 3);
    }
  }
}

/**
 * A marked minefield, stippled like the tilled ground and corner stakes the world shows (01 §5). One
 * pixel in four: a field can swallow a whole compound, and at any denser a hatch it buries the goal
 * zone's own outline instead of warning about the ground under it.
 */
function drawMines(p: Paper, rects: readonly Rect[]): void {
  p.g.fillStyle = MAP_COLOURS.mine;
  for (const rect of rects) {
    const x0 = clamp(Math.round(Math.min(rect.x0, rect.x1) * p.scale), 0, p.width - 1);
    const z0 = clamp(Math.round(Math.min(rect.z0, rect.z1) * p.scale), 0, p.height - 1);
    const x1 = clamp(Math.round(Math.max(rect.x0, rect.x1) * p.scale), x0 + 1, p.width);
    const z1 = clamp(Math.round(Math.max(rect.z0, rect.z1) * p.scale), z0 + 1, p.height);
    for (let z = z0 + (z0 % 2); z < z1; z += 2) {
      for (let x = x0 + (x0 % 2); x < x1; x += 2) p.g.fillRect(x, z, 1, 1);
    }
  }
}

/**
 * Recon Photos (04 §8): each building's footprint, filled. Under the roads, the marks and everything
 * the mission is judged by, because it is the SHAPE of the place and not an instruction - a player
 * who reads a solid block as a target has been told a lie for five RP. Always at least a pixel: a
 * tent is 3.4 m across, which is two pixels on a long map and none at all on the shortest.
 */
function drawStructures(p: Paper, marks: readonly StructureMark[]): void {
  p.g.fillStyle = MAP_COLOURS.structure;
  for (const m of marks) {
    const x0 = clamp(Math.round((m.x - m.hx) * p.scale), 0, p.width - 1);
    const z0 = clamp(Math.round((m.z - m.hz) * p.scale), 0, p.height - 1);
    const x1 = clamp(Math.round((m.x + m.hx) * p.scale), x0 + 1, p.width);
    const z1 = clamp(Math.round((m.z + m.hz) * p.scale), z0 + 1, p.height);
    p.g.fillRect(x0, z0, x1 - x0, z1 - z0);
  }
}

/**
 * Patrol Schedule (04 §8): the closed ring each patrol walks, dashed, with its waypoints picked out.
 * Drawn LAST of the fixed layer. This is the card the play report was about - "I bought the patrol
 * routes intel but it showed nothing" - and a ring buried under a dashed zone rect would earn the
 * same sentence again. The corners are where a sentry turns and is looking the other way, so they
 * are worth a pixel of their own.
 */
function drawPatrols(p: Paper, routes: ReadonlyArray<ReadonlyArray<readonly [number, number]>>): void {
  p.g.strokeStyle = MAP_COLOURS.patrol;
  p.g.lineWidth = 1;
  p.g.setLineDash([2, 2]);
  for (const route of routes) {
    p.g.beginPath();
    route.forEach(([x, z], i) => {
      const px = x * p.scale;
      const pz = z * p.scale;
      if (i === 0) p.g.moveTo(px, pz);
      else p.g.lineTo(px, pz);
    });
    p.g.stroke();
  }
  p.g.setLineDash([]);
  p.g.fillStyle = MAP_COLOURS.patrol;
  for (const route of routes) {
    // The last point repeats the first (the ring is closed): marking it again would only thicken one corner.
    for (let i = 0; i < route.length - 1; i++) {
      const node = route[i];
      if (node === undefined) continue;
      const x = clamp(Math.round(node[0] * p.scale), 0, p.width - 1);
      const z = clamp(Math.round(node[1] * p.scale), 0, p.height - 1);
      p.g.fillRect(x, z, 1, 1);
    }
  }
}

/**
 * Inside Man (04 §8): the gate his man left open, in the same pale lane a ford and a pipe support get.
 * That colour already means one thing on this map - "the one place a barrier lets a man through" - and
 * an open gate is exactly that, so it says it the same way rather than inventing a fourth idiom.
 */
function drawOpenGate(p: Paper, gate: { x: number; z: number; width: number } | null): void {
  if (gate === null) return;
  const w = Math.max(2, Math.round(gate.width * p.scale));
  const x = clamp(Math.round(gate.x * p.scale) - Math.floor(w / 2), 0, p.width - w);
  const z = clamp(Math.round(gate.z * p.scale) - 1, 0, p.height - 3);
  p.g.fillStyle = MAP_COLOURS.crossing;
  p.g.fillRect(x, z, w, 3);
}

/** The boundary a mission is lost inside: an open circle, so it is plainly a limit and not a thing. */
function drawFailCircles(p: Paper, circles: readonly MapCircle[]): void {
  p.g.strokeStyle = MAP_COLOURS.danger;
  p.g.lineWidth = 1;
  p.g.setLineDash([1, 1]);
  for (const circle of circles) {
    p.g.beginPath();
    p.g.arc(circle.x * p.scale, circle.z * p.scale, Math.max(2, circle.r * p.scale), 0, Math.PI * 2);
    p.g.stroke();
  }
  p.g.setLineDash([]);
}

/** Ponds, water barriers, roads, trenches, minefields, the objective's zones and the goal zone. */
export function drawBackdrop(def: MissionDef, width: number, height: number, scale: number): HTMLCanvasElement {
  const layer = document.createElement('canvas');
  layer.width = width;
  layer.height = height;
  const g = layer.getContext('2d');
  if (g === null) return layer;
  const p: Paper = { g, scale, width, height };
  const plan: BackdropPlan = backdropPlan(def);
  g.lineCap = 'round';
  g.lineJoin = 'round';
  g.fillStyle = MAP_COLOURS.water;
  for (const pond of def.ponds) {
    g.beginPath();
    g.arc(pond.x * scale, pond.z * scale, Math.max(1, pond.r * scale), 0, Math.PI * 2);
    g.fill();
  }
  drawWater(p, plan.water);
  // Recon Photos: the buildings go down first of all, so everything else is read over them.
  drawStructures(p, plan.structures);
  // Roads last of the ground, so a bridge's road reads as crossing the river rather than stopping at it.
  strokeLines(p, def.roads, MAP_COLOURS.road, 3);
  strokeLines(p, def.trenches, MAP_COLOURS.trench, 1);
  drawPipes(p, plan.pipes);
  drawMines(p, plan.mines);
  drawFailCircles(p, plan.failCircles);
  // The objective's zones before the goal zone: where he is told to BE, over the ground he is told about.
  for (const zone of plan.zones) dashRect(p, zone, MAP_COLOURS.waypointZone, [2, 1]);
  dashRect(p, def.goalZone, MAP_COLOURS.goal, [2, 1]);
  drawOpenGate(p, plan.openGate);
  // Patrol Schedule last of everything: it is the one thing on this layer he paid for by name.
  drawPatrols(p, plan.patrols);
  return layer;
}
